


























































































































































































































































































import { parse } from 'date-fns';
import {
	computed,
	defineComponent,
	nextTick,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import deepequal from 'deep-equal';
import { ApiMonthOption } from '@front-app/components/inputs/monthPickerUtil';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import { FullUserOccupation } from '@front-app/services/UserApi/UserModel';
import { isNonEmpty } from '@front-app/util/object';
import { requiredIfBoth, maxLen, inRange } from '@front-app/components/form/validation/validators';
import { Size } from '@front-app/components/constants';
import { toNegOneOrString } from '@front-app/modules/profile/util/occupation';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { v } from '@front-app/components/form/validation/useValidation';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import MonthPicker from '@front-app/components/inputs/MonthPicker.vue';
import PhoneLink from '@front-app/components/PhoneLink.vue';
import SuccessCard from '@front-app/components/cards/state/SuccessCard.vue';
import ProfileCard from '../ProfileCard.vue';
import ProfileSubheader from '../ProfileSubheader.vue';
import TrAddress from '../../TrAddress.vue';
import { EditableSection } from '../editableProfileSections';

const YEAR_MONTH_EMPTY_STATE = ApiMonthOption.NONE;

function isValue(a: number | null): a is number {
	return Boolean(a && a > 0);
}

export default defineComponent({
	components: {
		CtaButton,
		ErrorCard,
		FormError,
		FormInput,
		FormLabel,
		FormSection,
		MonthPicker,
		PhoneLink,
		ProfileCard,
		ProfileSubheader,
		SuccessCard,
		TrAddress,
	},
	props: {
		isCurrentUserProfile: Boolean,
		isPresentOccupationPanelDisabled: Boolean,
		showProfessionalExperienceSection: Boolean,
		currentOccupation: {
			type: Object as PropType<FullUserOccupation>,
			required: true,
		},
		editButtonLabel: {
			type: String,
			default: 'edit-button',
		},
		professionalExperience: {
			type: Array as PropType<FullUserOccupation[]>,
			required: true,
		},
	},
	setup(props, ctx) {
		const hasStartDate = computed(() => {
			const { startMonth, startYear } = props.currentOccupation;
			return isValue(startMonth) || isValue(startYear);
		});
		const formatDateYearAndMonth = (year: number, month: number) => {
			const parsedDate = parse(`${year}-${month}`, 'y-M', new Date(0, 0, 0, 0, 0, 0, 0));
			return ctx.root.$d(parsedDate, 'yearAndMonth');
		};
		const occupationStart = computed(() => {
			const { startMonth, startYear } = props.currentOccupation;
			if (isValue(startYear) && isValue(startMonth)) {
				return formatDateYearAndMonth(startYear, startMonth);
			}
			if (isValue(startYear)) {
				const parsedDate = parse(`${startYear}`, 'y', new Date(0, 0, 0, 0, 0, 0, 0));
				return ctx.root.$d(parsedDate, 'year');
			}
			if (isValue(startMonth)) {
				const parsedDate = parse(`${startMonth}`, 'M', new Date(0, 0, 0, 0, 0, 0, 0));
				return ctx.root.$d(parsedDate, 'month');
			}
			return '';
		});
		const hasEmployerAddress = computed(() => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { contactInfo, ...rest } = props.currentOccupation.employer.address;
			return isNonEmpty(rest);
		});
		const hasMyLocationAddress = computed(() => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { contactInfo, ...rest } = props.currentOccupation.location;
			return isNonEmpty(rest);
		});
		const hasContact = computed(() => isNonEmpty(props.currentOccupation.employer.address.contactInfo));
		const hasLocation = computed(() => isNonEmpty(props.currentOccupation.location));
		const hasAllDates = (item: FullUserOccupation) => isValue(item.startYear)
			&& isValue(item.startMonth)
			&& isValue(item.endYear)
			&& isValue(item.endMonth);

		// Logic to hide/show each section
		const showProfileSection = computed(() => hasStartDate.value || props.currentOccupation.positionTitle || props.isCurrentUserProfile);
		const showOrganizationSectionForOthers = computed(() => hasEmployerAddress.value || props.currentOccupation.employer.type || hasContact.value);
		const showOrganizationSection = computed(() => showOrganizationSectionForOthers.value || props.isCurrentUserProfile);
		const addressIsSameAsAbove = computed(() => deepequal(props.currentOccupation.employer.address, props.currentOccupation.location));
		const showLocationSection = computed(() => (props.isCurrentUserProfile && showOrganizationSectionForOthers.value) || (hasLocation.value && !addressIsSameAsAbove.value));
		const showComponent = computed(() => showProfileSection.value || showOrganizationSection.value || showLocationSection.value);

		// Present occupation
		const { currentUser } = CurrentUserStore;
		const actualPositionTitle = computed(() => currentUser.value.currentOccupation.positionTitle);
		const positionTitle = ref('');
		watch(actualPositionTitle, (curr: string) => {
			positionTitle.value = curr;
		}, { immediate: true });

		const minYear = new Date().getFullYear() - 100;
		const maxYear = new Date().getFullYear();
		const actualStartYear = computed(() => currentUser.value.currentOccupation.startYear);
		const startYear: Ref<number | null> = ref(null);
		watch(actualStartYear, (curr: number | null) => {
			if (!curr || curr < minYear || curr > maxYear) {
				startYear.value = null;
			} else {
				startYear.value = curr;
			}
		}, { immediate: true });

		const actualStartMonth = computed(() => currentUser.value.currentOccupation.startMonth);
		const startMonth: Ref<number | null> = ref(null);
		watch(actualStartMonth, (curr: number | null) => {
			startMonth.value = curr;
		}, { immediate: true });

		// Validation
		const yearRangeValidator = inRange(minYear, maxYear);
		const validation = v({
			positionTitle: {
				value: positionTitle,
				rules: [maxLen(100)],
			},
			startYear: {
				value: startYear,
				rules: [
					requiredIfBoth(() => startMonth.value, [YEAR_MONTH_EMPTY_STATE]),
					yearRangeValidator,
				],
			},
		});
		const {
			focusFirstInvalid,
		} = useFocusFirstInvalidField({ containerSelector: '.current-occupation__update-form' });

		const {
			track,
			isError: isErrorSavingPresentOccupation,
			isLoading: isSavingPresentOccupation,
			isSuccess: isSuccessSavingPresentOccupation,
		} = useAsyncStatus();

		const savePresentOccupation = async () => {
			validation.validate();
			if (validation.isValid.value) {
				const currentOccupation = {
					positionTitle: positionTitle.value,
					startMonth: toNegOneOrString(startMonth.value),
					startYear: toNegOneOrString(startYear.value),
				};
				track(CurrentUserStore.actions.update({ currentOccupation }));
			} else {
				// Focus the first error field
				await nextTick();
				focusFirstInvalid();
			}
		};

		return {
			// Visibility
			showProfileSection,
			showOrganizationSection,
			showLocationSection,
			showComponent,
			// Start Date
			hasStartDate,
			occupationStart,
			// Address
			hasEmployerAddress,
			hasContact,
			// Personal Location
			hasLocation,
			addressIsSameAsAbove,
			hasMyLocationAddress,
			// Present occupation
			positionTitle,
			startMonth,
			startYear,
			savePresentOccupation,
			// Status
			isErrorSavingPresentOccupation,
			isSavingPresentOccupation,
			isSuccessSavingPresentOccupation,
			// Validation
			minYear,
			maxYear,
			validation,
			yearRangeValidator,
			YEAR_MONTH_EMPTY_STATE,
			// Util
			ButtonPalettes,
			EditableSection,
			isNonEmpty,
			hasAllDates,
			formatDateYearAndMonth,
			Size,
		};
	},
});
